import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { subscribe, useSnapshot } from 'valtio';
import { mapState as mapViewState } from '~/components2/Map/Map';
import { getCurrentlyVisibleLegendLayerIds } from '~/components2/Map/Widgets/Legend/getCurrentlyVisibleLegendLayerIds';
import layerStore from '~/store2/layers';
import { permittingEmbedMapRef } from './permittingMapSnapshot';
import { getVisiblePermittingLegendModels } from './permittingVisibleLegend';

/**
 * Compact legend: same map visibility rules as the main `Legend` (`queryRenderedFeatures` + rasters),
 * limited to permitting utility layers. Sits below the map (6-column grid); print uses `buildPermittingPrintLegendHtml`.
 */
export function PermittingCondensedLegend() {
  const { tilesConfig } = useSnapshot(layerStore) as typeof layerStore;
  const [mapVisibleIds, setMapVisibleIds] = useState<string[]>([]);

  const refreshLegendList = useMemo(
    () =>
      debounce(() => {
        setMapVisibleIds(getCurrentlyVisibleLegendLayerIds(permittingEmbedMapRef.current));
      }, 250),
    [],
  );

  useEffect(() => {
    const unsubView = subscribe(mapViewState, () => refreshLegendList());
    const unsubLayers = subscribe(layerStore, () => refreshLegendList());
    const timeout = setTimeout(refreshLegendList, 500);
    refreshLegendList();
    return () => {
      unsubView();
      unsubLayers();
      refreshLegendList.cancel();
      clearTimeout(timeout);
    };
  }, [refreshLegendList]);

  const blocks = tilesConfig ? getVisiblePermittingLegendModels(tilesConfig, mapVisibleIds) : [];

  if (!tilesConfig) {
    return (
      <div className="pointer-events-auto z-[1] flex min-h-[100px] w-full items-center rounded border border-grey-30 bg-white/95 px-2 py-1.5 font-noto text-[10px] text-grey-70 shadow-sm print:hidden">
        Legend unavailable
      </div>
    );
  }

  return (
    <section
      className="pointer-events-auto z-[1] min-h-[100px] w-full rounded border border-grey-30 bg-white/95 px-2 py-1.5 font-noto text-[10px] shadow-sm print:hidden"
      aria-label="Map legend"
    >
      <div className="grid grid-cols-6 gap-x-2 gap-y-2">
        <div className="min-w-0">
          <div className="mb-0.5 font-medium leading-tight text-grey-80">Project</div>
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-4 shrink-0 rounded-sm border-2 border-dashed border-brand bg-transparent"
              aria-hidden
            />
            <span className="leading-tight text-grey-90">Project area</span>
          </div>
        </div>

        {blocks.length === 0 ? (
          <div className="col-span-5 min-w-0 self-center text-[12px] leading-snug text-grey-70">
            Zoom in to see data
          </div>
        ) : (
          blocks.map((block) => (
            <div key={block.layerId} className="min-w-0">
              <div className="mb-0.5 font-medium leading-tight text-grey-80">{block.title}</div>
              <div className="flex flex-col">
                {block.rows.map((row) => (
                  <div
                    key={`${block.layerId}-${row.label}-${row.imageData.slice(0, 12)}`}
                    className="flex items-center gap-1.5"
                  >
                    <img
                      src={`data:image/jpeg;base64,${row.imageData}`}
                      alt=""
                      width={row.width}
                      height={row.height}
                      className="mt-0.5 h-4 w-4 shrink-0 object-contain"
                    />
                    <span className="min-w-0 flex-1 leading-snug text-grey-90">{row.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
